/**
 * Definicion del personaje.
 *
 * Toda la apariencia del avatar vive en este objeto. Cambiar el personaje
 * (color de piel, peinado, barba, gafas, traje, anchura de la cara) es editar
 * datos, no dibujo: la ilustracion se recompone sola a partir de estos valores.
 *
 * Las variantes actuales estan construidas sobre las fotografias de referencia
 * aprobadas por la universidad. Para ajustar el parecido no hace falta rehacer
 * el rig ni la sincronizacion labial: basta con retocar estos valores.
 */

export type HairStyle = 'corto' | 'entradas' | 'peinado' | 'canoso';
export type FacialHair = 'ninguna' | 'incipiente' | 'bigote' | 'barba';

export interface Character {
  id: string;
  /** Nombre de la variante, para la pantalla de aprobacion. */
  label: string;
  /** Una linea describiendo la intencion de la variante. */
  note: string;

  skin: string;
  skinShade: string;
  skinLine: string;

  hair: string;
  hairShade: string;
  hairStyle: HairStyle;
  /** Canas en las sienes. null si no las lleva. */
  temples: string | null;

  brow: string;
  eye: string;
  facialHair: FacialHair;
  /** El vello facial suele tener otro tono que el pelo: encanece antes. */
  beard: string;

  glasses: boolean;
  glassesFrame: string;

  suit: string;
  suitShade: string;
  shirt: string;
  /** null = camisa sin corbata, con el cuello abierto. */
  tie: string | null;

  lip: string;
  lipLine: string;

  /** Multiplicador de la anchura de la cara. 1 = base; >1 = rostro mas lleno. */
  faceWidth: number;
}

/** Rasgos comunes a todas las variantes: es la misma persona. */
const base = {
  skin: '#D9A97C',
  skinShade: '#C08F60',
  skinLine: '#A0744A',

  hair: '#8C8781',
  hairShade: '#6B665F',
  hairStyle: 'peinado' as HairStyle,
  temples: '#E2DFDA',

  brow: '#4A4038',
  eye: '#4A3B2E',
  facialHair: 'barba' as FacialHair,
  beard: '#D6D2CC',

  glassesFrame: '#3A3A42',

  suit: '#26304E',
  suitShade: '#1A2238',
  shirt: '#FDFDFB',

  lip: '#B5806C',
  lipLine: '#8C5A48',

  faceWidth: 1.1,
};

/**
 * Variantes para la aprobacion del personaje.
 *
 * Las tres son la misma persona; cambia el registro de vestuario y las gafas,
 * que es lo unico que la universidad tiene que decidir.
 */
export const CHARACTERS: Character[] = [
  {
    ...base,
    id: 'rector',
    label: 'Variante A — Como en las fotografias',
    note: 'Traje azul marino con camisa blanca de cuello abierto, sin gafas. Es el registro de las fotos de referencia.',
    glasses: false,
    tie: null,
  },
  {
    ...base,
    id: 'rector-formal',
    label: 'Variante B — Registro institucional',
    note: 'El mismo personaje con corbata en el azul del CEC. Mas formal, para un acto o una portada.',
    glasses: false,
    tie: '#000099',
  },
  {
    ...base,
    id: 'rector-gafas',
    label: 'Variante C — Con gafas',
    note: 'Con montura fina y corbata. Util si se prefiere una imagen mas academica.',
    glasses: true,
    tie: '#000099',
  },
];

export const DEFAULT_CHARACTER = CHARACTERS[0]!;

export function characterById(id: string | null | undefined): Character {
  return CHARACTERS.find((c) => c.id === id) ?? DEFAULT_CHARACTER;
}
